/**
 * Tom semântico de um status. Mora aqui porque é aqui que ele é derivado (do
 * `code`); a tradução para classes fica em `event.config.ts`.
 */
export type GroupTone = 'approved' | 'rejected' | 'pending';

/**
 * Parametrização que a API devolve em `GET /approval/config` — o documento que o
 * ticketformbuilder-site grava em `inteegra_lp.approval.mapping`.
 *
 * Todo campo é opcional: isto vem de `jsonb`, não de um `import` de JSON. Quem
 * converte para a configuração da tela (`toRuntimeEventConfig`) valida o que usa e
 * falha com mensagem em vez de renderizar um painel pela metade.
 *
 * O que o documento **não** traz, e por isso continua em `panel.settings.json`: as
 * transições permitidas entre status, a ação em lote, o tamanho de página e os
 * rótulos em 3ª pessoa das mensagens.
 */
export interface ApprovalMappingDocument {
  eventCode?: number;
  title?: string | null;
  /** Cor da marca no painel, em hexadecimal. Dela sai a escala `--color-brand-*`. */
  themeColor?: string | null;
  attributes?: number[];
  /** `{ [idDoAtributo]: { name, order } }` — as colunas do grid. */
  columns?: Record<string, { name?: string | null; order?: number }>;
  profileAttributeId?: number | null;
  profiles?: Array<{ name?: string | null; order?: number }>;
  /** Indexado pela chave do status; `miceId` é o grupo no MICE. */
  status?: Record<string, { name?: string | null; order?: number; miceId?: number; code?: string | null }>;
}

/** Coluna do grid: o id do atributo é a chave do valor em `Participant.values`. */
export interface RuntimeColumn {
  attributeId: number;
  label: string;
}

/** Aba da listagem. `key` é o que vai na query `grupo`. */
export interface RuntimeGroup {
  key: string;
  label: string;
  miceId: number;
  tone: GroupTone;
}

/** Trilha. `name` é o que vai na query `perfil`. */
export interface RuntimeProfile {
  name: string;
  label: string;
}

export interface RuntimeEventConfig {
  /** Título do evento, como o wizard o gravou. */
  title: string;
  columns: RuntimeColumn[];
  groups: RuntimeGroup[];
  profiles: RuntimeProfile[];
  /** `grupoId` do participante → chave do status, para inferir a aba de cada linha. */
  groupByMiceId: Map<number, string>;
}

/** Parametrização inválida ou incompleta: a tela mostra a mensagem e oferece recarregar. */
export class EventConfigError extends Error {}

/**
 * Tom de cada status a partir do status de confirmação (`code`), que é o único
 * campo do documento com essa informação. Sem ele — ou com um código novo — o
 * status entra como pendente: neutro é melhor que verde ou vermelho errados.
 */
const TONE_BY_CODE: Record<string, GroupTone> = {
  CN: 'approved',
  CA: 'rejected',
  PE: 'pending',
};

function byOrder(first: { order: number | undefined }, second: { order: number | undefined }): number {
  return (first.order ?? 0) - (second.order ?? 0);
}

/**
 * Converte o documento da API na configuração que os componentes consomem.
 *
 * `fallbackTitle` cobre documento gravado sem título: melhor o nome que o painel
 * já conhecia do que um cabeçalho vazio.
 */
export function toRuntimeEventConfig(document: ApprovalMappingDocument, { fallbackTitle = '' }: { fallbackTitle?: string } = {}): RuntimeEventConfig {
  const columns: RuntimeColumn[] = Object.entries(document.columns ?? {})
    .map(([attributeId, column]) => ({
      attributeId: Number(attributeId),
      label: column?.name?.trim() || `Atributo ${attributeId}`,
      order: column?.order,
    }))
    .filter(column => Number.isInteger(column.attributeId) && column.attributeId > 0)
    .sort(byOrder)
    .map(({ attributeId, label }) => ({ attributeId, label }));

  const groups: RuntimeGroup[] = Object.entries(document.status ?? {})
    .map(([key, status]) => ({
      key,
      label: status?.name?.trim() || key,
      miceId: status?.miceId ?? 0,
      tone: TONE_BY_CODE[status?.code?.trim().toUpperCase() ?? ''] ?? 'pending',
      order: status?.order,
    }))
    .filter(group => Number.isInteger(group.miceId) && group.miceId > 0)
    .sort(byOrder)
    .map(({ key, label, miceId, tone }) => ({ key, label, miceId, tone }));

  if (groups.length === 0) {
    throw new EventConfigError('A parametrização do evento não tem nenhum status com grupo do MICE definido.');
  }

  const profiles: RuntimeProfile[] = (document.profiles ?? [])
    .map(profile => ({ name: profile?.name?.trim() ?? '', label: profile?.name?.trim() ?? '', order: profile?.order }))
    .filter(profile => profile.name.length > 0)
    .sort(byOrder)
    .map(({ name, label }) => ({ name, label }));

  return {
    title: document.title?.trim() || fallbackTitle,
    columns,
    groups,
    profiles,
    groupByMiceId: new Map(groups.map(group => [group.miceId, group.key])),
  };
}
